import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useUser } from 'src/components/async/Users'
import {
  AccessManagementAbsoluteRoutes,
  AiGrcQaAbsoluteRoutes,
  AuditBotAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  DirectRoutes,
  EvidenceAbsoluteRoutes,
  ExamineAndTestAbsoluteRoutes,
  FrameworksAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ReceptorsAbsoluteRoutes,
  RequestsAbsoluteRoutes,
  RisksAbsoluteRoutes,
  RoadmapAbsoluteRoutes,
  SchedulingAbsoluteRoutes,
  ScopeAbsoluteRoutes,
  SecurityQuestionnaireAbsoluteRoutes,
  ServicesAbsoluteRoutes,
  TrustPortalConfigurationAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  UserRolesAbsoluteRoutes,
  VendorsAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { useAuth, Permissions } from 'src/context/authContext'
import { isAuditorBase } from './AppAuth.config'

export const checkPath = (routes: string[], path: string): boolean => {
  for (const route of routes) {
    const regex = new RegExp(`^/[a-z0-9:]+/${route}(?:/|$)`)
    if (regex.test(path)) {
      return true
    }
  }
  return false
}

export const isShowPageRoute = (path: string): boolean => {
  return /\/show\/[^/]+/.test(path)
}

/**
 * Every route a HAS_PAGE_ACCESS capability governs, so denying the capability hides the
 * nav entry and bounces the route. A capability only belongs here if it gates a page:
 * Chats, Playbooks, Departments and Content Versions have no page of their own - they
 * gate features inside other pages (the copilot panel, the copilot library, the
 * department pickers and the frameworks page), which their CAN_* capabilities cover.
 */
export const PAGE_ACCESS_CAPABILITY_ROUTES: Partial<
  Record<CAPABILITY, string[]>
> = {
  [CAPABILITY.HAS_PAGE_ACCESS_CONTROLS]: [ControlsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_POLICIES]: [PoliciesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_EVIDENCE]: [EvidenceAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RISKS]: [RisksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_SERVICES]: [ServicesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RECEPTORS]: [ReceptorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_FRAMEWORKS]: [FrameworksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDITS]: [DirectRoutes.AUDITS],
  [CAPABILITY.HAS_PAGE_ACCESS_REQUESTS]: [RequestsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_REPORTS]: [DirectRoutes.REPORTS],
  [CAPABILITY.HAS_PAGE_ACCESS_ROADMAP]: [RoadmapAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS]: [
    DirectRoutes.DASHBOARD,
    DirectRoutes.GRC_DASHBOARDS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_USERS]: [DirectRoutes.USERS],
  [CAPABILITY.HAS_PAGE_ACCESS_SCOPE]: [ScopeAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDIT_SCANS]: [
    ExamineAndTestAbsoluteRoutes.ROOT,
    AuditBotAbsoluteRoutes.ROOT,
    ExamineAndTestAbsoluteRoutes.COMPLIANCE_ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUST_PORTAL]: [
    TrustPortalConfigurationAbsoluteRoutes.ROOT,
    AccessManagementAbsoluteRoutes.ROOT,
  ],
  // security_questionnaire is the deprecated route the live ai_grc_qa page replaced;
  // both still resolve, so both have to be denied together.
  [CAPABILITY.HAS_PAGE_ACCESS_QUESTIONNAIRES]: [
    SecurityQuestionnaireAbsoluteRoutes.ROOT,
    AiGrcQaAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_SETTINGS]: [
    DirectRoutes.ACCOUNT_SETTINGS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_VENDORS]: [VendorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_COMPANY_INFO]: [DirectRoutes.COMPANY_INFO],
  [CAPABILITY.HAS_PAGE_ACCESS_SCHEDULES]: [SchedulingAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUSTERO_INTELLIGENCE]: [
    TrusteroIntelligenceAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_DETAILS]: [
    DirectRoutes.ACTIVITIES,
    DirectRoutes.USAGE,
    DirectRoutes.INDEXING,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCESS_ROLES]: [UserRolesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_GRC_DOCUMENTS]: [DirectRoutes.MANAGE_DOCUMENTS],
}

// The order a "land on a page this role can open" flow walks: the dashboard first
// because it is the app's home, then everything else the registry knows about.
const PAGE_ACCESS_LANDING_ORDER: CAPABILITY[] = [
  CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS,
  ...(Object.keys(PAGE_ACCESS_CAPABILITY_ROUTES).map(Number) as CAPABILITY[]),
]

/**
 * The first page route the caller's role may open, or undefined when it may open none.
 * `isPageAccessAllowed` answers for a single HAS_PAGE_ACCESS capability.
 */
export const firstAllowedPageRoute = (
  isPageAccessAllowed: (capability: CAPABILITY) => boolean,
  isRouteAllowed: (route: string) => boolean = () => true,
): string | undefined => {
  for (const capability of PAGE_ACCESS_LANDING_ORDER) {
    const routes = PAGE_ACCESS_CAPABILITY_ROUTES[capability]
    if (routes?.length && isPageAccessAllowed(capability)) {
      const route = routes.find(isRouteAllowed)
      if (route) return route
    }
  }
  return undefined
}

export const useValidateRouteAccess = (isAuthLoading: boolean): void => {
  const denyRoutes = useDeniedRoutes()
  const location = useLocation()
  const { permissions, accountId } = useAuth()

  useEffect(() => {
    if (
      isAuthLoading ||
      !permissions ||
      location.pathname.split('/')[1]?.split(':')[0] !== accountId
    ) {
      return
    }
    const path = location.pathname

    if (checkPath(denyRoutes, path) && !isShowPageRoute(path)) {
      window.location.href = DirectRoutes.ACCESS_DENIED
    }
  }, [location.pathname, denyRoutes, isAuthLoading, permissions, accountId])
}

// An audit assignment is an exact identifier, not a substring match. Accounts and
// profile remain reachable so a user with no assignments can switch accounts or leave.
export const auditRedirectPath = ({
  accountId,
  auditId,
  allowedAudits,
  pathname,
  canViewAudits,
}: {
  accountId: string
  auditId?: string
  allowedAudits: string[]
  pathname: string
  canViewAudits: boolean
}): string | undefined => {
  if (checkPath([DirectRoutes.ACCOUNTS, DirectRoutes.PROFILE], pathname)) return
  // Assignment satisfies audit scope only; useValidateRouteAccess and show-page
  // guards still enforce page capabilities. canViewAudits below only gates the
  // fallback redirect to Audits, not other pages within an assigned audit.
  if (auditId && allowedAudits.includes(auditId)) return
  if (!canViewAudits) return DirectRoutes.ACCESS_DENIED
  const context = allowedAudits.length
    ? `${accountId}:${allowedAudits[0]}`
    : accountId
  const target = `/${context}/${DirectRoutes.AUDITS}`
  return pathname.replace(/\/$/, '') === target ? undefined : target
}

export const useRestrictAccessToAudit = (): boolean => {
  const { permissions, accountId, isAuthLoading } = useAuth()
  const { data, error } = useUser()
  const params = useParams()
  const location = useLocation()
  const restrict = isAuditorBase(permissions?.baseAccessRole)
  const [urlAccountId, auditId] = (params.pageContext || '').split(':')
  const matchingAccount = urlAccountId === accountId
  const matchingUser = data?.getAccountid() === accountId
  const canViewAudits = checkCanPerformAction(
    CAPABILITY.HAS_PAGE_ACCESS_AUDITS,
    undefined,
    permissions,
    data,
  )
  const target =
    restrict && matchingAccount && !isAuthLoading
      ? error
        ? DirectRoutes.ACCESS_DENIED
        : matchingUser
        ? auditRedirectPath({
            accountId,
            auditId,
            allowedAudits: data.getAuditIdsList(),
            pathname: location.pathname,
            canViewAudits,
          })
        : undefined
      : undefined

  useEffect(() => {
    if (target) window.location.href = `${target}${location.search}`
  }, [target, location.search])

  return restrict && matchingAccount && (!matchingUser || !!target)
}

// Trust Portal has two independently readable surfaces under one page capability.
const ROUTE_VIEW_CAPABILITIES: Record<string, CAPABILITY> = {
  [TrustPortalConfigurationAbsoluteRoutes.ROOT]:
    CAPABILITY.CAN_VIEW_CONTENT_MANAGEMENT,
  [AccessManagementAbsoluteRoutes.ROOT]: CAPABILITY.CAN_VIEW_ACCESS_MANAGEMENT,
}

export const getDeniedRoutes = (
  permissions?: Permissions,
  user?: { getEmail?: () => string; getId?: () => string } | null,
): string[] => {
  const can = (cap: CAPABILITY) =>
    checkCanPerformAction(cap, undefined, permissions, user)
  const deniedRoutes = Object.entries(PAGE_ACCESS_CAPABILITY_ROUTES).flatMap(
    ([cap, routes]) =>
      (routes || []).filter(
        (route) =>
          !can(Number(cap) as CAPABILITY) ||
          (ROUTE_VIEW_CAPABILITIES[route] !== undefined &&
            !can(ROUTE_VIEW_CAPABILITIES[route])),
      ),
  )
  return Array.from(new Set(deniedRoutes))
}

// Route guards and navigation share the same capability decisions.
export const useDeniedRoutes = (): string[] => {
  const { permissions } = useAuth()
  const { data: user } = useUser()
  return useMemo(() => getDeniedRoutes(permissions, user), [permissions, user])
}

export const useIsAuditRestricted = (): boolean => {
  const { permissions } = useAuth()
  return isAuditorBase(permissions?.baseAccessRole)
}

export const useLandingRoute = (hasGrcDashboards: boolean): string => {
  const { permissions } = useAuth()
  const can = useCanPerformActionChecker()
  if (isAuditorBase(permissions?.baseAccessRole)) {
    return can(CAPABILITY.HAS_PAGE_ACCESS_AUDITS)
      ? DirectRoutes.AUDITS
      : DirectRoutes.ACCESS_DENIED
  }
  const route = firstAllowedPageRoute(
    can,
    (route) =>
      ROUTE_VIEW_CAPABILITIES[route] === undefined ||
      can(ROUTE_VIEW_CAPABILITIES[route]),
  )
  return route === DirectRoutes.DASHBOARD && hasGrcDashboards
    ? DirectRoutes.GRC_DASHBOARDS
    : route || DirectRoutes.ACCESS_DENIED
}

export type ResourceWithAuthz = {
  ownerEmail?: string
  ownerId?: string
  assigneeEmailsList?: string[]
  assigneeEmails?: string[]
  labelIdsList?: string[]
  labelIds?: string[]
  getOwnerEmail?: () => string
  getOwnerId?: () => string
  getAssigneeEmailsList?: () => string[]
  getLabelIdsList?: () => string[]
}

export const checkCanPerformAction = <T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
  permissions?: Permissions | null,
  user?: {
    getEmail?: () => string
    getId?: () => string
  } | null,
): boolean => {
  if (!permissions) return false

  const capabilityInfo = permissions?.capabilities?.[capability]
  if (!capabilityInfo) return false

  // User Role ALWAYS and NEVER should never be affected by label permission.
  if (capabilityInfo.rule === PERMISSION_RULE.ALWAYS) {
    return true
  }
  if (capabilityInfo.rule === PERMISSION_RULE.NEVER) {
    return false
  }

  const getResourceLabels = (): string[] => {
    if (!resource) return []
    if (typeof resource.getLabelIdsList === 'function') {
      return resource.getLabelIdsList() || []
    }
    return resource.labelIdsList || resource.labelIds || []
  }

  const getResourceOwnerEmail = (): string | undefined => {
    if (!resource) return undefined
    if (typeof resource.getOwnerEmail === 'function') {
      return resource.getOwnerEmail()
    }
    return resource.ownerEmail
  }

  const getResourceOwnerId = (): string | undefined => {
    if (!resource) return undefined
    if (typeof resource.getOwnerId === 'function') {
      return resource.getOwnerId()
    }
    return resource.ownerId
  }

  const getResourceAssignees = (): string[] => {
    if (!resource) return []
    if (typeof resource.getAssigneeEmailsList === 'function') {
      return resource.getAssigneeEmailsList() || []
    }
    return resource.assigneeEmailsList || resource.assigneeEmails || []
  }

  // 2. CONTROLLED_BY_LABEL asks the labels, and nothing else does.
  //
  // A deny label always refuses, and beats an allow label on the same resource. Past
  // that, the allow set decides whether this is a blocklist or an allowlist: holding no
  // allow labels leaves the capability unscoped and allows by default, so a group with
  // only deny labels blocks what it names and permits the rest. Holding any allow label
  // narrows it to the objects those labels cover.
  //
  // Keep aligned with rbac.CheckResourceAccess -- testdata/resource_access_parity.json
  // runs the same cases against both.
  if (capabilityInfo.rule === PERMISSION_RULE.CONTROLLED_BY_LABEL) {
    // Empty ids are dropped so a blank entry in a grant list cannot match a resource
    // whose label id is also blank. Mirrors rbac.toSet.
    const labelSet = (ids?: string[]) => new Set((ids || []).filter(Boolean))
    const denyLabels = labelSet(permissions.denyLabelIds)
    const allowLabels = labelSet(permissions.allowLabelIds)
    const resourceLabelIds = getResourceLabels()

    if (resourceLabelIds.some((id) => denyLabels.has(id))) {
      return false
    }
    if (allowLabels.size === 0) {
      return true
    }
    return resourceLabelIds.some((id) => allowLabels.has(id))
  }

  // 3. Fall back to the capability's own rule, which asks about the actor rather than
  // the object.
  //
  // IF_TRUSTERO_USER grants on Trustero staff. Mirrors rbac.IsTrusteroUser.
  if (capabilityInfo.rule === PERMISSION_RULE.IF_TRUSTERO_USER) {
    const email =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    return !!email && email.toLowerCase().endsWith('@trustero.com')
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_OWNER) {
    const ownerEmail = getResourceOwnerEmail()
    const ownerId = getResourceOwnerId()
    const userEmail =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    const userId = typeof user?.getId === 'function' ? user.getId() : undefined
    return (
      (!!ownerEmail && ownerEmail === userEmail) ||
      (!!ownerId && ownerId === userId)
    )
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_OWNER_OR_ASSIGNEE) {
    const ownerEmail = getResourceOwnerEmail()
    const ownerId = getResourceOwnerId()
    const userEmail =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    const userId = typeof user?.getId === 'function' ? user.getId() : undefined
    const isOwner =
      (!!ownerEmail && ownerEmail === userEmail) ||
      (!!ownerId && ownerId === userId)
    const isAssignee = !!userEmail && getResourceAssignees().includes(userEmail)
    return isOwner || isAssignee
  }
  return false
}

export const useCanPerformActionChecker = (): (<T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
) => boolean) => {
  const { permissions } = useAuth()
  const { data: user } = useUser()

  return useCallback(
    (capability: CAPABILITY, resource?: ResourceWithAuthz) =>
      checkCanPerformAction(capability, resource, permissions, user),
    [permissions, user],
  )
}

export const useCanPerformAction = <T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
): boolean => {
  const check = useCanPerformActionChecker()
  return check(capability, resource)
}

// Shared action controls use this during the role-config migration. A caller that
// knows the domain supplies its required capabilities; the component owns the
// consistent AND and resource-scope evaluation.
// The or-form of useHasRequiredCapabilities, for an action an rpc admits on any of
// several capabilities -- a relationship named from both sides, say. An empty or absent
// list is vacuously satisfied, matching rbac.HasAnyCapability, so passing none does not
// deny.
export const useHasAnyCapability = <T extends ResourceWithAuthz>(
  anyOfCapabilities?: CAPABILITY[],
  resource?: T,
): boolean => {
  const canPerformAction = useCanPerformActionChecker()
  if (!anyOfCapabilities?.length) return true
  return anyOfCapabilities.some((capability) =>
    canPerformAction(capability, resource),
  )
}

export const useHasRequiredCapabilities = <T extends ResourceWithAuthz>(
  requiredCapabilities?: CAPABILITY[],
  resource?: T,
): boolean => {
  const canPerformAction = useCanPerformActionChecker()
  return (
    requiredCapabilities?.every((capability) =>
      canPerformAction(capability, resource),
    ) ?? true
  )
}
